import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Link,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    CircularProgress,
    Alert,
    useTheme as useMuiTheme,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import {
    getTableHeaderStyles,
    getTableContainerStyles,
    getTableRowStyles,
    getTableCellStyles,
} from '../styles/tableStyles';
import { StatMiniCard, DashboardCard } from './ChannelDashboard';
// W-6: Shared utilities — no duplicate implementations across the two dashboards.
import { downloadCsv, MultiSelectFilter, ConfirmActionDialog } from '../utils/dashboardShared';
import { getTaFilters, getTaRecords, closeChangeRequest, postAclAction, TaDashboardRecord, TaFilterOptions, TaSummaryCounts } from '../services/shayDashboardService';
// PR review fix (M1): extracted so the new/remediated bucketing logic is unit-testable
// independently of this component's rendering and API calls.
import { buildFindingsOverTime } from '../helpers/trustedAdvisorDerivations';

// W-2: Keys are lowercase so lookup works regardless of API capitalisation.
const STATUS_ROW_COLOR: Record<string, string> = {
    'action recommended': 'rgba(255, 224, 224, 0.5)',
    'investigation recommended': 'rgba(255, 243, 205, 0.5)',
    'ok': 'rgba(212, 237, 218, 0.5)',
};
const UNKNOWN_ROW_COLOR = 'rgba(200, 200, 200, 0.15)';
const statusRowBg = (status: string | null | undefined): string =>
    STATUS_ROW_COLOR[(status || '').toLowerCase()] ?? UNKNOWN_ROW_COLOR;

const EMPTY_FILTER_OPTIONS: TaFilterOptions = { report_names: [], categories: [], statuses: [] };

/** Matches the mockup's "<p class='hint'>" — a one-line description under each panel title. */
const PanelHint = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5, mt: -1 }}>{children}</Typography>
);

/** Shows up to 3 lines of text with a "View more / View less" toggle. Only renders the
 *  toggle anchor when the content actually overflows the 3-line clamp. */
const ExpandableCell: React.FC<{ text: string | null | undefined }> = ({ text }) => {
    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);
    const ref = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        if (!expanded && ref.current) {
            setOverflows(ref.current.scrollHeight > ref.current.clientHeight + 2);
        }
    }, [text, expanded]);

    if (!text) return <>—</>;
    return (
        <Box sx={{ minWidth: 220, maxWidth: 340 }}>
            <Typography
                ref={ref}
                variant="body2"
                component="p"
                sx={{
                    fontSize: '0.8rem',
                    m: 0,
                    ...(expanded ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' } : {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                    }),
                }}
            >
                {text}
            </Typography>
            {(overflows || expanded) && (
                <Link
                    component="button"
                    variant="caption"
                    onClick={() => setExpanded(e => !e)}
                    sx={{ mt: 0.5, display: 'inline-block', p: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                >
                    {expanded ? 'View less' : 'View more'}
                </Link>
            )}
        </Box>
    );
};

interface TrustedAdvisorDashboardProps {
    /** B-2: Forwarded to the API so the backend can scope results to this channel. */
    channelId?: string;
}

const TrustedAdvisorDashboard: React.FC<TrustedAdvisorDashboardProps> = ({ channelId }) => {
    const { isDarkMode } = useTheme();
    const muiTheme = useMuiTheme();
    const palette = muiTheme.palette;

    const [records, setRecords] = useState<TaDashboardRecord[]>([]);
    const [summaryCounts, setSummaryCounts] = useState<TaSummaryCounts>({});
    const [filterOptions, setFilterOptions] = useState<TaFilterOptions>(EMPTY_FILTER_OPTIONS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [reportFilter, setReportFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [closeTicketLoading, setCloseTicketLoading] = useState<Record<string, boolean>>({});
    const [closeTicketDone, setCloseTicketDone] = useState<Record<string, boolean>>({});
    const [closeTicketError, setCloseTicketError] = useState<Record<string, string>>({});
    const [applyRemediationLoading, setApplyRemediationLoading] = useState<Record<string, boolean>>({});
    const [applyRemediationDone, setApplyRemediationDone] = useState<Record<string, boolean>>({});
    const [applyRemediationError, setApplyRemediationError] = useState<Record<string, string>>({});
    const [closeTicketConfirmTarget, setCloseTicketConfirmTarget] = useState<TaDashboardRecord | null>(null);

    useEffect(() => {
        // W-1 + W-5: AbortController cancels in-flight fetches on unmount / rapid remount,
        // preventing both stale state updates and piled-up abandoned network requests.
        const controller = new AbortController();
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const [filters, taResponse] = await Promise.all([
                    getTaFilters(controller.signal),
                    // B-2: pass channelId so the backend can scope results server-side.
                    getTaRecords({}, channelId, controller.signal),
                ]);
                setFilterOptions(filters);
                setRecords(taResponse.records);
                setSummaryCounts(taResponse.summaryCounts);
            } catch (err: unknown) { // M-2: unknown instead of any
                // If the request was aborted (e.g. component unmounted or React StrictMode
                // double-invocation in dev), skip all state updates — the component is
                // either gone or about to re-run the effect cleanly.
                if (controller.signal.aborted) return;
                console.error('Failed to load Trusted Advisor dashboard data', err);
                // W-3: Distinguish auth failure from a transient server error.
                const msg = err instanceof Error ? err.message : '';
                if (msg === 'HTTP_401' || msg === 'HTTP_403') {
                    setError('You are not authorised to view Trusted Advisor data.');
                } else {
                    setError('Failed to load Trusted Advisor data. Please try again.');
                }
            } finally {
                // Guard: `finally` runs even when `catch` returns, so only update
                // loading state if this effect instance was not aborted.
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        load();
        return () => controller.abort();
    }, [channelId]);

    // Filter option lists come from /ta/filters; fall back to values seen in the fetched
    // records in case the filters endpoint omits a field the records actually contain.
    const reportOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.report_name).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.report_names, ...fromRecords])].sort();
    }, [filterOptions, records]);
    const categoryOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.category).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.categories, ...fromRecords])].sort();
    }, [filterOptions, records]);
    const statusOptions = useMemo(() => {
        const fromRecords = [...new Set(records.map(r => r.status).filter((v): v is string => !!v))];
        return [...new Set([...filterOptions.statuses, ...fromRecords])].sort();
    }, [filterOptions, records]);

    const filtered = useMemo(() => {
        return records.filter((r) => {
            if (reportFilter.length && !(r.report_name && reportFilter.includes(r.report_name))) return false;
            if (categoryFilter.length && !(r.category && categoryFilter.includes(r.category))) return false;
            if (statusFilter.length && !(r.status && statusFilter.includes(r.status))) return false;
            return true;
        });
    }, [records, reportFilter, categoryFilter, statusFilter]);

    const totalFindings = filtered.length;
    const reportsCount = new Set(filtered.map(r => r.report_name)).size;
    const categoriesCount = new Set(filtered.map(r => r.category)).size;
    const actionRequired = filtered.filter(r => (r.status || '').toLowerCase().includes('action')).length;
    // Mirrors actionRequired's derivation — purely computed from the existing status field,
    // no new API field needed (ref: tvs_aiops_dashboards.html mockup's "Investigations" card).
    const investigationsRequired = filtered.filter(r => (r.status || '').toLowerCase().includes('investigat')).length;

    // LIVE — ticket_id/ticket_status are now sent by the backend.
    const openChangeRequests = filtered.filter(r => (r.ticket_status || '').toLowerCase() === 'open').length;
    const closedChangeRequests = filtered.filter(r => (r.ticket_status || '').toLowerCase() === 'closed').length;
    const noTicketCount = filtered.filter(r => !r.ticket_id).length;

    const byCategory = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            const key = r.category || '(not set)';
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.count - b.count);
    }, [filtered]);

    const byStatus = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            const key = r.status || '(not set)';
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [filtered]);

    const heatmap = useMemo(() => {
        const rows: Record<string, Record<string, number>> = {};
        filtered.forEach(r => {
            const cat = r.category || '(not set)';
            const status = r.status || '(not set)';
            rows[cat] = rows[cat] || {};
            rows[cat][status] = (rows[cat][status] || 0) + 1;
        });
        return rows;
    }, [filtered]);
    // Only show statuses that have a defined colour in STATUS_ROW_COLOR — excludes
    // test/unknown values (e.g. "test status") and keeps the heatmap to real categories.
    const heatmapStatuses = [...new Set(filtered.map(r => r.status || '(not set)'))]
        .filter(s => STATUS_ROW_COLOR[s.toLowerCase()] !== undefined);

    // NOT YET LIVE — depends on `account`, which the backend does not send yet. Falls back to
    // a single "(not set)" bucket holding every record until the field is populated.
    const byAccount = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            const key = r.account || '(not set)';
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.count - b.count);
    }, [filtered]);

    // LIVE — owner_team is now sent by the backend.
    const byOwner = useMemo(() => {
        const counts: Record<string, number> = {};
        filtered.forEach(r => {
            const key = r.owner_team || '(not set)';
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.count - b.count);
    }, [filtered]);

    // Single "Change Requests" row with 3 stacked series, now fed by live ticket_id/ticket_status.
    const changeRequestStatus = useMemo(() => [{
        name: 'Change Requests',
        open: openChangeRequests,
        closed: closedChangeRequests,
        noTicket: noTicketCount,
    }], [openChangeRequests, closedChangeRequests, noTicketCount]);

    // LIVE — first_seen_at is now sent by the backend. Any record still missing it is excluded
    // from aging/time-series buckets rather than guessed into one.
    const findingsAging = useMemo(() => {
        const buckets = { '0–7d': 0, '8–30d': 0, '31–60d': 0, '60d+': 0 };
        const now = Date.now();
        filtered.forEach(r => {
            if (!r.first_seen_at) return;
            const ageDays = (now - new Date(r.first_seen_at).getTime()) / (1000 * 60 * 60 * 24);
            if (Number.isNaN(ageDays)) return;
            if (ageDays <= 7) buckets['0–7d']++;
            else if (ageDays <= 30) buckets['8–30d']++;
            else if (ageDays <= 60) buckets['31–60d']++;
            else buckets['60d+']++;
        });
        return Object.entries(buckets).map(([name, count]) => ({ name, count }));
    }, [filtered]);

    // LIVE — "New findings" grouped by first_seen_at's month. "Remediated" is now also
    // derivable, using the newly-live closed_at field (previously omitted since no
    // remediation-date field existed at all). Logic lives in trustedAdvisorDerivations.ts,
    // unit-tested there (ref: PR review M1 — no coverage existed for this bucketing).
    const findingsOverTime = useMemo(() => buildFindingsOverTime(filtered), [filtered]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>;
    }

    const requestCloseTicket = (r: TaDashboardRecord) => {
        setCloseTicketConfirmTarget(r);
    };

    const handleCloseTicket = async () => {
        const r = closeTicketConfirmTarget;
        if (!r) return;
        const key = r.ticket_id ?? String(r.id);
        setCloseTicketConfirmTarget(null);
        setCloseTicketLoading(prev => ({ ...prev, [key]: true }));
        setCloseTicketError(prev => { const n = { ...prev }; delete n[key]; return n; });
        try {
            await closeChangeRequest({
                channel_id: channelId ?? '',
                change_request_id: r.ticket_id ?? '',
                comment: r.summary ?? '',
                assignment_group: r.assignment_group ?? '',
                close_code: 'successful',
                force_approve: true,
            });
            setCloseTicketDone(prev => ({ ...prev, [key]: true }));
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to close ticket';
            setCloseTicketError(prev => ({ ...prev, [key]: msg }));
        } finally {
            setCloseTicketLoading(prev => { const n = { ...prev }; delete n[key]; return n; });
        }
    };

    const handleApplyRemediation = async (r: TaDashboardRecord) => {
        const key = r.ticket_id ?? String(r.id);
        setApplyRemediationLoading(prev => ({ ...prev, [key]: true }));
        setApplyRemediationError(prev => { const n = { ...prev }; delete n[key]; return n; });
        try {
            await postAclAction({
                request_type: 'apply_remediation',
                report_name: r.report_name ?? '',
                category: r.category ?? '',
                status: r.status ?? '',
                recommendations: r.recommendations ?? r.summary ?? '',
                remediation: r.remediation ?? '',
                resource: r.resource ?? '',
                responsibility: r.responsibility ?? '',
                action: r.action ?? '',
            });
            setApplyRemediationDone(prev => ({ ...prev, [key]: true }));
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to apply remediation';
            setApplyRemediationError(prev => ({ ...prev, [key]: msg }));
        } finally {
            setApplyRemediationLoading(prev => { const n = { ...prev }; delete n[key]; return n; });
        }
    };

    return (
        <Box>
            <Accordion sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: '12px !important', boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Additional Filters</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                        <MultiSelectFilter label="Report" options={reportOptions} value={reportFilter} onChange={setReportFilter} />
                        <MultiSelectFilter label="Category" options={categoryOptions} value={categoryFilter} onChange={setCategoryFilter} />
                        <MultiSelectFilter label="Status" options={statusOptions} value={statusFilter} onChange={setStatusFilter} />
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, letterSpacing: '0.05em' }}>Summary</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
                <StatMiniCard
                    title="Total Findings"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'text.primary' }}>{totalFindings}</Typography>
                            {/* Hardcoded per explicit request — not derived from any API field. */}
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>across 1 account</Typography>
                        </Box>
                    }
                />
                <StatMiniCard title="Reports" content={<Box><Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'text.primary' }}>{reportsCount}</Typography></Box>} />
                <StatMiniCard title="Categories" content={<Box><Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'text.primary' }}>{categoriesCount}</Typography></Box>} />
                {/* accent-red / accent-amber per tvs_aiops_dashboards.html mockup — matches the
                    same red/amber severity meaning already used in Findings by Status and the
                    Heatmap on this same dashboard. */}
                <StatMiniCard
                    title="Action Required"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'error.main' }}>{actionRequired}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {Math.round(summaryCounts.action_percentage ?? 0)}% of findings
                            </Typography>
                        </Box>
                    }
                />
                <StatMiniCard
                    title="Investigations"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '2.125rem', color: 'warning.main' }}>{investigationsRequired}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {summaryCounts.investigations_awaiting_approval ?? 0} awaiting approval
                            </Typography>
                        </Box>
                    }
                />
                <StatMiniCard
                    title="Open Change Requests"
                    content={
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.875rem', color: 'text.primary' }}>{openChangeRequests}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                {summaryCounts.closed_change_requests ?? closedChangeRequests} closed · {summaryCounts.open_change_requests ?? noTicketCount} no ticket
                            </Typography>
                        </Box>
                    }
                />
            </Box>

            {totalFindings === 0 ? (
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}>
                    <Typography color="text.secondary">No Trusted Advisor records match the selected filters.</Typography>
                </Paper>
            ) : (
                <>
                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, letterSpacing: '0.05em' }}>Breakdown</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
                        <DashboardCard title="Findings by Category">
                            <PanelHint>Where risk is concentrated</PanelHint>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={byCategory} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: palette.text.primary }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                        labelStyle={{ color: palette.text.secondary }}
                                    />
                                    <Bar dataKey="count" fill={palette.primary.main} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Findings by Status">
                            <PanelHint>Actions vs investigations vs info</PanelHint>
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85}>
                                        {byStatus.map((entry) => {
                                            const key = entry.name.toLowerCase();
                                            const fill = key.includes('action') ? palette.error.main
                                                : key.includes('investigat') ? palette.warning.main
                                                : key === 'ok' ? palette.success.main
                                                : palette.grey[400];
                                            return <Cell key={entry.name} fill={fill} stroke={palette.background.paper} strokeWidth={2} />;
                                        })}
                                    </Pie>
                                    <RechartsTooltip
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            {/* Legend outside SVG so long labels never clip against the card edge */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, px: 1, pb: 1 }}>
                                {byStatus.map((entry) => {
                                    const key = entry.name.toLowerCase();
                                    const color = key.includes('action') ? palette.error.main
                                        : key.includes('investigat') ? palette.warning.main
                                        : key === 'ok' ? palette.success.main
                                        : palette.grey[400];
                                    return (
                                        <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                                {entry.name}: <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{entry.value}</Box>
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </DashboardCard>

                        <DashboardCard title="Findings by Account">
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={byAccount} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: palette.text.primary }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                        labelStyle={{ color: palette.text.secondary }}
                                    />
                                    <Bar dataKey="count" fill={palette.info.main} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
                        <DashboardCard title="Findings by Owner/Team">
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={byOwner} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: palette.text.primary }} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                        labelStyle={{ color: palette.text.secondary }}
                                    />
                                    <Bar dataKey="count" fill={palette.primary.main} radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Change Request Status">
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={changeRequestStatus} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: palette.text.secondary }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                    <RechartsLegend wrapperStyle={{ fontSize: 11 }} />
                                    <Bar dataKey="open" name="Open" stackId="cr" fill={palette.warning.main} radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="closed" name="Closed" stackId="cr" fill={palette.success.main} radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="noTicket" name="No ticket" stackId="cr" fill={palette.grey[400]} radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>

                        <DashboardCard title="Open-Finding Aging">
                            <PanelHint>SLA posture by days open</PanelHint>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={findingsAging} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: palette.text.secondary }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} axisLine={false} tickLine={false} />
                                    <RechartsTooltip
                                        cursor={{ fill: palette.action.hover }}
                                        contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}`, borderRadius: 8, boxShadow: muiTheme.shadows[8] }}
                                        itemStyle={{ color: palette.text.primary }}
                                    />
                                    {/* Severity gradient per bucket (fresher = greener, older = redder), matching mockup */}
                                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                        {findingsAging.map((entry, i) => (
                                            <Cell key={entry.name} fill={[palette.success.main, palette.warning.main, palette.warning.dark, palette.error.main][i] ?? palette.grey[400]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </DashboardCard>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <DashboardCard title="Findings Over Time">
                            <PanelHint>Req #1 — date/time per run; new vs remediated</PanelHint>
                            {findingsOverTime.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                                    No dated findings yet — this chart will populate once each finding's first_seen_at is available.
                                </Typography>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    {/* AreaChart (not LineChart) to match the mockup's smoothed, shaded-under-curve
                                        style — type="monotone" gives the curve, fill gives the translucent shading. */}
                                    <AreaChart data={findingsOverTime} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={palette.divider} />
                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: palette.text.secondary }} />
                                        <YAxis tick={{ fontSize: 11, fill: palette.text.secondary }} allowDecimals={false} />
                                        <RechartsTooltip contentStyle={{ backgroundColor: palette.background.paper, border: `1px solid ${palette.divider}` }} />
                                        <RechartsLegend wrapperStyle={{ fontSize: 11 }} />
                                        <Area type="monotone" dataKey="newFindings" name="New findings" stroke={palette.error.main} fill={palette.error.main} fillOpacity={0.08} strokeWidth={2} dot />
                                        <Area type="monotone" dataKey="remediated" name="Remediated" stroke={palette.success.main} fill={palette.success.main} fillOpacity={0.08} strokeWidth={2} dot />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </DashboardCard>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <DashboardCard title="Category × Status Heatmap">
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles() }}>Category</TableCell>
                                            {heatmapStatuses.map((s) => (
                                                <TableCell key={s} align="center" sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles() }}>{s}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.keys(heatmap).map((cat) => (
                                            <TableRow key={cat}>
                                                <TableCell sx={getTableCellStyles()}>{cat}</TableCell>
                                                {heatmapStatuses.map((s) => {
                                                    const count = heatmap[cat][s] || 0;
                                                    return (
                                                        <TableCell
                                                            key={s}
                                                            align="center"
                                                            sx={{
                                                                ...getTableCellStyles(),
                                                                bgcolor: count > 0 ? UNKNOWN_ROW_COLOR : 'transparent',
                                                                fontWeight: count > 0 ? 700 : 400,
                                                                color: count > 0 ? 'text.primary' : 'text.disabled',
                                                            }}
                                                        >
                                                            {count}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </DashboardCard>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ color: 'text.secondary', letterSpacing: '0.05em' }}>Detailed Records</Typography>
                        <Button size="small" variant="outlined" onClick={() => downloadCsv(filtered, 'ta_data.csv')}>Download CSV</Button>
                    </Box>
                    {/* overflow: 'auto' overrides getTableContainerStyles()'s 'hidden' — otherwise
                        maxHeight clips long cell content (e.g. long Summary text) instead of
                        making it scrollable within the table (ref: Detailed Records scroller fix). */}
                    <TableContainer component={Paper} sx={{ ...getTableContainerStyles(), boxShadow: 2, maxHeight: 420, overflow: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {['ID', 'Account', 'Category', 'Status', 'Owner/Team', 'Ticket', 'Remediation', 'Summary', 'Report', 'Apply Remediation', 'Close Ticket'].map((h) => (
                                        <TableCell key={h} sx={{ ...getTableHeaderStyles(isDarkMode, true), ...getTableCellStyles() }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.map((r, idx) => (
                                    // M-3: id is the natural key; idx fallback is safe — this list is never reordered in place.
                                    <TableRow key={r.id ?? idx} hover sx={{ ...getTableRowStyles(), bgcolor: statusRowBg(r.status) }}>
                                        <TableCell sx={getTableCellStyles()}>{r.id ?? '—'}</TableCell>
                                        <TableCell sx={getTableCellStyles()}>{r.account || '—'}</TableCell>
                                        <TableCell sx={getTableCellStyles()}>{r.category || '—'}</TableCell>
                                        <TableCell sx={getTableCellStyles()}>{r.status || '—'}</TableCell>
                                        <TableCell sx={getTableCellStyles()}>{r.owner_team || '—'}</TableCell>
                                        <TableCell sx={getTableCellStyles()}>{r.ticket_id || '—'}</TableCell>
                                        <TableCell sx={{ ...getTableCellStyles(), verticalAlign: 'top' }}><ExpandableCell text={r.remediation} /></TableCell>
                                        <TableCell sx={{ ...getTableCellStyles(), verticalAlign: 'top' }}><ExpandableCell text={r.summary} /></TableCell>
                                        <TableCell sx={getTableCellStyles()}>{r.report_name || '—'}</TableCell>
                                        <TableCell sx={getTableCellStyles()}>
                                            {(() => {
                                                const key = r.ticket_id ?? String(r.id);
                                                if (r.summary) {
                                                    return <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>Applied Remediation</Button>;
                                                }
                                                if (applyRemediationDone[key]) {
                                                    return <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}>Submitted</Button>;
                                                }
                                                return (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={applyRemediationLoading[key]}
                                                            onClick={() => handleApplyRemediation(r)}
                                                            sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                                        >
                                                            {applyRemediationLoading[key] ? 'Submitting...' : 'Apply Remediation'}
                                                        </Button>
                                                        {applyRemediationError[key] && (
                                                            <Box sx={{ fontSize: '0.65rem', color: 'error.main', mt: 0.5 }}>{applyRemediationError[key]}</Box>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell sx={getTableCellStyles()}>
                                            {(() => {
                                                const key = r.ticket_id ?? String(r.id);
                                                const isClosed = (r.ticket_status || '').toLowerCase() === 'closed';
                                                if (closeTicketDone[key] || isClosed) {
                                                    return <Button variant="outlined" size="small" disabled sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem', color: 'success.main' }}>Closed</Button>;
                                                }
                                                return (
                                                    <>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            disabled={closeTicketLoading[key] || isClosed}
                                                            onClick={() => requestCloseTicket(r)}
                                                            sx={{ whiteSpace: 'nowrap', fontSize: '0.7rem' }}
                                                        >
                                                            {closeTicketLoading[key] ? 'Closing...' : 'Close Ticket'}
                                                        </Button>
                                                        {closeTicketError[key] && (
                                                            <Box sx={{ fontSize: '0.65rem', color: 'error.main', mt: 0.5 }}>{closeTicketError[key]}</Box>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            <ConfirmActionDialog
                open={!!closeTicketConfirmTarget}
                title="Close Ticket"
                message={
                    <>
                        Are you sure you want to close ticket{' '}
                        <strong>{closeTicketConfirmTarget?.ticket_id || 'this ticket'}</strong>?
                    </>
                }
                caveat="This will close the ServiceNow change request and cannot be undone."
                confirmLabel="Close Ticket"
                confirmingLabel="Closing..."
                confirming={!!closeTicketConfirmTarget && !!closeTicketLoading[closeTicketConfirmTarget.ticket_id ?? String(closeTicketConfirmTarget.id)]}
                onConfirm={handleCloseTicket}
                onClose={() => setCloseTicketConfirmTarget(null)}
            />
        </Box>
    );
};

export default TrustedAdvisorDashboard;
